import { useEffect, useRef, useState } from 'react'
import { ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Line, Tooltip, Legend, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import { getChartPalette, getModelColorMap } from '../../chartPalette'

const colors = getChartPalette()
const MODEL_COLORS = getModelColorMap()

const MAX_BAR_SIZE = 90

// Fixed color mapping for account types
const fixedColorMap = {
  Dealer: MODEL_COLORS['Dealer'],
  Coltene: MODEL_COLORS['Coltene'],
  Technician: MODEL_COLORS['Technician'],
  Dentist: MODEL_COLORS['Dentist'],
  'Customer Service': MODEL_COLORS['Customer Service'],
  Admin: MODEL_COLORS['Admin'],
  'Company Account': MODEL_COLORS['Company Account'],
  'Dealer Technician': MODEL_COLORS['Dealer Technician'],
  'Technical Service': MODEL_COLORS['Technical Service'],
  Sales: MODEL_COLORS['Sales'],
  Support: MODEL_COLORS['Support'],
  Partner: MODEL_COLORS['Partner'],
  Distributor: MODEL_COLORS['Distributor'],
}

const CustomLegend = ({ payload }) => {
  if (!payload || payload.length === 0) return null

  return (
    <div className="custom-legend-container">
      {payload.map((entry) => (
        <div className="legend-item" key={entry.value}>
          <span className="legend-color-dot" style={{ background: entry.color }} />
          <span className="legend-label">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}

const RemoteAccessReportChart = ({ data = [], chartType = 'line' }) => {
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  const chartData = Array.isArray(data) && data.length ? data : [{ date: '' }]
  const rawModelKeys = Array.from(new Set(data.flatMap((row) => Object.keys(row).filter((k) => k !== 'date'))))

  // Sort modelKeys according to fixedColorMap order, then others alphabetically
  const fixedKeysOrder = Object.keys(fixedColorMap)
  const modelKeys = rawModelKeys.sort((a, b) => {
    const aIndex = fixedKeysOrder.indexOf(a)
    const bIndex = fixedKeysOrder.indexOf(b)

    // Both are fixed keys, sort by fixedColorMap order
    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex
    }
    // a is fixed, b is not: a comes first
    if (aIndex !== -1) return -1
    // b is fixed, a is not: b comes first
    if (bIndex !== -1) return 1
    // Neither is fixed, sort alphabetically
    return a.localeCompare(b)
  })

  const colorMap = {}
  const usedFixedColors = new Set()
  let colorIndex = 0

  modelKeys.forEach((model) => {
    // Check if this model has a fixed color assignment
    if (fixedColorMap[model]) {
      colorMap[model] = fixedColorMap[model]
      usedFixedColors.add(fixedColorMap[model])
    } else {
      // Assign next available color from the palette
      while (colorIndex < colors.length && usedFixedColors.has(colors[colorIndex])) {
        colorIndex++
      }
      if (colorIndex < colors.length) {
        colorMap[model] = colors[colorIndex]
        usedFixedColors.add(colors[colorIndex])
        colorIndex++
      } else {
        // Fallback: cycle through all colors if we run out
        colorMap[model] = colors[(colorIndex - Object.keys(fixedColorMap).length) % colors.length]
        colorIndex++
      }
    }
  })

  const renderChart = () => {
    switch (chartType) {
      case 'bar':
        return (
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={CustomLegend} />
            {modelKeys.map((model, idx) => (
              <Bar
                maxBarSize={MAX_BAR_SIZE}
                key={model}
                dataKey={model}
                fill={colorMap[model]}
                name={model}
                activeShape={(props) => <rect {...props} fill="transparent" stroke="none" />}
              />
            ))}
          </BarChart>
        )
      case 'pie':
        const pieData = modelKeys.map((model) => ({
          name: model,
          value: chartData.reduce((sum, row) => sum + Number(row[model] || 0), 0),
        }))

        return (
          <PieChart>
            <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="53%" outerRadius={250} label isAnimationActive={false}>
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={colorMap[entry.name]} />
              ))}
            </Pie>
            <Legend content={CustomLegend} />
            <Tooltip />
          </PieChart>
        )
      case 'stackedBar':
        return (
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={CustomLegend} />
            {modelKeys.map((model, idx) => (
              <Bar key={model} dataKey={model} maxBarSize={MAX_BAR_SIZE} fill={colorMap[model]} name={model} stackId="total" />
            ))}
          </BarChart>
        )
      case 'line':
      default:
        return (
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend content={CustomLegend} />
            {modelKeys.map((model, idx) => (
              <Line
                key={model}
                type="monotone"
                dataKey={model}
                stroke={colorMap[model]}
                strokeWidth={2}
                activeDot={{ r: 5 }}
                dot={false}
                name={model}
                isAnimationActive={false}
              />
            ))}
          </LineChart>
        )
    }
  }

  useEffect(() => {
    const observer = new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} style={{ width: '100%', height: 650 }} className="chart-responsive-wrapper">
      <div className="chart-min-width">
        {dimensions.width > 0 && dimensions.height > 0 && (
          <ResponsiveContainer width="100%" height="100%" key={`${dimensions.width}x${dimensions.height}`}>
            {renderChart()}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export default RemoteAccessReportChart

